import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useLogin } from "@/hooks/useLogin";
import { useAuth } from "@/context/AuthContext";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Loader2, X, Clock } from "lucide-react";

const APP_NAME = import.meta.env.VITE_APP_NAME || "Hospital Management System";

// Fix #12: CSS in module-level constant, not inline string soup
// Fix #9:  double-class specificity instead of !important
// Fix #15: 100dvh for mobile browsers
const STYLES = `
  .lp-root { display: flex; min-height: 100dvh; }

  .lp-panel {
    width: 44%;
    display: flex;
    flex-direction: column;
    padding: 3rem 3.5rem;
    background: hsl(197, 38%, 9%);
    position: relative;
    overflow: hidden;
  }

  .lp-grid {
    position: absolute; inset: 0; pointer-events: none;
    background-image:
      linear-gradient(hsla(197,50%,30%,0.12) 1px, transparent 1px),
      linear-gradient(90deg, hsla(197,50%,30%,0.12) 1px, transparent 1px);
    background-size: 36px 36px;
  }

  .lp-ecg {
    position: absolute; bottom: 35%; left: 0; width: 100%; height: 56px; overflow: visible;
  }

  @keyframes ecg-trace {
    0%   { stroke-dashoffset: 900; opacity: 0; }
    5%   { opacity: 0.55; }
    78%  { stroke-dashoffset: 0; opacity: 0.55; }
    88%  { opacity: 0; }
    95%  { stroke-dashoffset: 900; opacity: 0; }
    100% { stroke-dashoffset: 900; opacity: 0; }
  }
  .lp-ecg-line {
    stroke-dasharray: 900;
    stroke-dashoffset: 900;
    animation: ecg-trace 5s cubic-bezier(0.4,0,0.2,1) infinite;
    animation-delay: 1s;
  }

  .lp-right {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 2.5rem 2rem;
    background: hsl(var(--background));
  }

  .lp-form-wrap { width: 100%; max-width: 372px; }

  @keyframes lp-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .lp-a { animation: lp-up 0.45s ease both; animation-delay: 0.05s; }
  .lp-b { animation: lp-up 0.45s ease both; animation-delay: 0.15s; }
  .lp-c { animation: lp-up 0.45s ease both; animation-delay: 0.25s; }
  .lp-d { animation: lp-up 0.45s ease both; animation-delay: 0.35s; }
  .lp-e { animation: lp-up 0.45s ease both; animation-delay: 0.45s; }
  .lp-f { animation: lp-up 0.45s ease both; animation-delay: 0.55s; }

  .lp-label {
    display: block;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: hsl(var(--muted-foreground));
    margin-bottom: 0.5rem;
  }

  /* Fix #9: double-class specificity instead of !important */
  .lp-input.lp-input {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1.5px solid hsl(var(--border));
    border-radius: 0;
    padding: 0.375rem 0 0.5rem;
    font-size: 0.9375rem;
    color: hsl(var(--foreground));
    box-shadow: none;
    outline: none;
    transition: border-color 0.18s ease;
  }
  .lp-input.lp-input:focus        { border-bottom-color: hsl(var(--primary)); box-shadow: none; }
  .lp-input.lp-input.lp-err      { border-bottom-color: hsl(var(--destructive)); }
  .lp-input.lp-input::placeholder { color: hsl(var(--muted-foreground)); opacity: 0.55; }

  /* Fix #10: removed fragile .lp-pw-wrap > div selector — not needed */

  /* Fix #13: mobile teal accent bar */
  .lp-mobile-bar {
    height: 4px;
    background: hsl(var(--primary));
    width: 100%;
    display: none;
  }

  @media (max-width: 1023px) {
    .lp-panel { display: none; }
    .lp-mobile-bar { display: block; }
  }
`;

export default function LoginPage() {
  const { login, loading, error, setError } = useLogin();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailRef = useRef<HTMLInputElement>(null);

  const sessionExpired = searchParams.get('reason') === 'expired';

  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [rememberMe, setRememberMe]   = useState(false);
  const [submitting, setSubmitting]   = useState(false);

  const getRoleRoute = (user: typeof currentUser): string => {
    if (!user) return "/login";
    if (user.must_change_password) return "/change-password";
    if (user.doctor_id) return "/";
    if (user.role === "staff") return "/frontdesk";
    if (user.role === "admin") return "/settings";
    return "/";
  };

  // Fix #3: load font via <link> in <head>, not @import in injected <style>
  useEffect(() => {
    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&display=swap";
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = `Sign in | ${APP_NAME}`;
    return () => { document.title = prev; };
  }, []);

  useEffect(() => { emailRef.current?.focus(); }, []);
  useEffect(() => { if (error) emailRef.current?.focus(); }, [error]);
  useEffect(() => {
    if (currentUser) navigate(getRoleRoute(currentUser), { replace: true });
  }, [currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setSubmitting(true);
    try { await login(email, password, rememberMe); }
    catch (err) { console.error("Login failed:", err); }
    finally { setSubmitting(false); }
  };

  if (currentUser) return null;

  const busy   = loading || submitting;
  const hasErr = !!error;
  const errId  = "login-error";

  return (
    <>
      <style>{STYLES}</style>

      {/* Fix #13: teal top bar visible only on mobile */}
      <div className="lp-mobile-bar" aria-hidden="true" />

      <div className="lp-root">

        {/* ── Left: Dark clinical panel ── */}
        <aside className="lp-panel">
          <div className="lp-grid" />

          {/* Fix #4: removed preserveAspectRatio="none" */}
          <svg className="lp-ecg" viewBox="0 0 520 56" aria-hidden="true">
            <polyline
              className="lp-ecg-line"
              points="0,28 70,28 95,28 106,6 116,50 124,6 132,44 142,28 220,28 252,28 266,3 280,53 292,3 300,42 312,28 390,28 450,28 520,28"
              stroke="hsl(197,85%,55%)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>

          {/* Logo — with text fallback if image fails */}
          <div style={{ position: 'relative', zIndex: 1, marginBottom: '3.5rem' }}>
            <img
              src="/onecheckup-logo-primary.png"
              alt=""
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              style={{ width: '172px', objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.92, display: 'block' }}
            />
            <span style={{ display: 'none', fontSize: '1.125rem', fontWeight: 700, color: 'hsl(0,0%,95%)', letterSpacing: '-0.02em' }}
              ref={el => {
                if (el) {
                  const img = el.previousElementSibling as HTMLImageElement;
                  img.onload = () => { el.style.display = 'none'; };
                  if (img.complete && img.naturalWidth === 0) el.style.display = 'block';
                }
              }}
            >
              OneCheckUp
            </span>
          </div>

          {/* Headline */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <p style={{ fontSize: '0.6875rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'hsl(197,60%,52%)', fontWeight: 600, marginBottom: '1.25rem' }}>
              For Indian clinics
            </p>
            <h2 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 'clamp(1.875rem, 2.4vw, 2.5rem)', fontWeight: 400, lineHeight: 1.22, color: 'hsl(0,0%,95%)', maxWidth: '340px', marginBottom: '1.25rem' }}>
              Built for the way<br />
              <em style={{ color: 'hsl(197,70%,62%)', fontStyle: 'italic' }}>Indian clinics work.</em>
            </h2>
            <p style={{ fontSize: '0.8125rem', color: 'hsl(197,20%,50%)', lineHeight: 1.8, maxWidth: '300px', margin: 0 }}>
              From the morning queue to the last prescription — every patient, every bill, every record in one place.
            </p>
          </div>

          {/* Footer strip — pushed to bottom */}
          <div style={{ position: 'relative', zIndex: 1, marginTop: 'auto', paddingTop: '1.5rem', borderTop: '1px solid hsl(197,20%,17%)', display: 'flex', gap: '2rem' }}>
            {[['Queue','Appointments & tokens'],['Rx','Prescriptions & templates'],['Billing','Invoices & payments']].map(([v, l], i) => (
              <div key={i}>
                <p style={{ color: 'hsl(197,70%,60%)', fontSize: '0.75rem', fontWeight: 700, margin: 0, letterSpacing: '0.04em' }}>{v}</p>
                <p style={{ color: 'hsl(197,12%,38%)', fontSize: '0.625rem', margin: '0.2rem 0 0', lineHeight: 1.4 }}>{l}</p>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Right: Form ── */}
        <main className="lp-right">
          {/* Mobile logo */}
          <div className="lg:hidden" style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img src="/onecheckup-logo-primary.png" alt={APP_NAME} style={{ width: '192px', objectFit: 'contain' }} />
          </div>

          <div className="lp-form-wrap">

            {/* Session-expired banner */}
            {sessionExpired && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'hsl(var(--warning) / 0.08)', border: '1px solid hsl(var(--warning) / 0.25)', marginBottom: '1.5rem' }}>
                <Clock style={{ width: '15px', height: '15px', flexShrink: 0, marginTop: '1px', color: 'hsl(var(--warning))' }} aria-hidden="true" />
                <p style={{ flex: 1, fontSize: '0.8125rem', color: 'hsl(var(--warning))', margin: 0, lineHeight: 1.5 }}>
                  Your session has expired. Please sign in to continue.
                </p>
              </div>
            )}

            {/* Heading */}
            <div className="lp-a" style={{ marginBottom: '2.5rem' }}>
              <h1 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: '1.75rem', fontWeight: 400, color: 'hsl(var(--foreground))', letterSpacing: '-0.01em', lineHeight: 1.2, marginBottom: '0.375rem' }}>
                Sign in
              </h1>
              <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', margin: 0 }}>
                Your clinic is waiting.
              </p>
            </div>

            {/* Fix #8: noValidate prevents browser native validation UI */}
            <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1.625rem' }}>

              {/* Email — Fix #7: aria-describedby wired */}
              <div className="lp-b">
                <label htmlFor="email" className="lp-label">Email</label>
                <input
                  ref={emailRef}
                  id="email"
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (error) setError(null); }}
                  disabled={busy}
                  required
                  autoComplete="email"
                  aria-describedby={hasErr ? errId : undefined}
                  aria-invalid={hasErr}
                  className={`lp-input${hasErr ? ' lp-err' : ''}`}
                />
              </div>

              {/* Password — Fix #7: aria-describedby wired */}
              <div className="lp-c">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                  <label htmlFor="password" className="lp-label" style={{ margin: 0 }}>Password</label>
                  <Link
                    to="/forgot-password"
                    style={{ fontSize: '0.75rem', color: 'hsl(var(--primary))', fontWeight: 500, textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                  >
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => { setPassword(e.target.value); if (error) setError(null); }}
                  disabled={busy}
                  required
                  autoComplete="current-password"
                  aria-describedby={hasErr ? errId : undefined}
                  aria-invalid={hasErr}
                  className={`lp-input${hasErr ? ' lp-err' : ''}`}
                />
              </div>

              {/* Fix #2: correct stagger — remember me = lp-d */}
              <div className="lp-d" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '-0.5rem' }}>
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={v => setRememberMe(v === true)}
                  disabled={busy}
                />
                <label htmlFor="remember" style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', userSelect: 'none' }}>
                  Remember me
                </label>
              </div>

              {/* Fix #5: role="alert" + aria-live; Fix #6: aria-label on dismiss */}
              {error && (
                <div
                  id={errId}
                  role="alert"
                  aria-live="assertive"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'hsl(var(--destructive) / 0.06)', border: '1px solid hsl(var(--destructive) / 0.2)' }}
                >
                  <AlertTriangle style={{ width: '15px', height: '15px', flexShrink: 0, marginTop: '1px', color: 'hsl(var(--destructive))' }} aria-hidden="true" />
                  <p style={{ flex: 1, fontSize: '0.8125rem', color: 'hsl(var(--destructive))', margin: 0 }}>
                    {error === "Invalid email or password"
                      ? "Incorrect email or password. Please try again."
                      : error}
                  </p>
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    aria-label="Dismiss error"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'hsl(var(--destructive))', opacity: 0.6, lineHeight: 1 }}
                  >
                    <X style={{ width: '14px', height: '14px' }} aria-hidden="true" />
                  </button>
                </div>
              )}

              {/* Fix #2: submit = lp-e; Fix #1: animate-spin className not inline style */}
              <div className="lp-e">
                <Button
                  type="submit"
                  disabled={busy}
                  style={{ width: '100%', height: '46px', borderRadius: '8px', fontWeight: 600, fontSize: '0.9375rem', letterSpacing: '0.01em' }}
                >
                  {busy
                    ? <><Loader2 className="animate-spin" style={{ width: '16px', height: '16px', marginRight: '8px' }} aria-hidden="true" />Signing in…</>
                    : 'Sign in'
                  }
                </Button>
              </div>
            </form>

            {/* Fix #2: footer = lp-f */}
            <div className="lp-f" style={{ marginTop: '2rem', textAlign: 'center' }}>
              <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
                Not on OneCheckUp yet?{' '}
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfOP9MUaGkdlKZNuD6Off1BYDz_ickWmEMVH4hOhqDkJz2GWA/viewform"
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: 'hsl(var(--primary))', fontWeight: 600, textDecoration: 'none' }}
                >
                  Get in touch
                </a>
              </p>
              <p style={{ marginTop: '0.75rem', fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', opacity: 0.55 }}>
                By signing in, you agree to our{' '}
                <Link to="/terms" style={{ textDecoration: 'underline', color: 'inherit' }}>Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" style={{ textDecoration: 'underline', color: 'inherit' }}>Privacy Policy</Link>.
              </p>
            </div>

          </div>
        </main>
      </div>
    </>
  );
}
